/**
 * POS register (S40) — Quick-picks + scan/search + cart + Charge, wired live to
 * `pos.quote` (GST-correct total) and `pos.ringSale` (the atomic counter sale).
 * Full-screen kiosk: no sidebar/topbar. @see prototype/pos/register.html
 */
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { WEB_ENV } from "../../lib/env.ts";
import { meQueryOptions } from "../../lib/session.ts";
import { formatRupees } from "../invoices/lib/format-money.ts";
import { PaymentSheet, type Tender } from "./payment-sheet.tsx";

interface CartLine {
  itemId: string;
  name: string;
  hsn: string;
  unit: string;
  mrpPaise: string; // tax-inclusive
  taxRateBps: number;
  qty: number;
}

const today = () => new Date().toISOString().slice(0, 10);
const initials = (s: string) =>
  s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "·";

export function RegisterPage({
  onCloseShift,
  onReturns,
}: { onCloseShift?: () => void; onReturns?: () => void } = {}) {
  const navigate = useNavigate();
  const me = useQuery(meQueryOptions);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState<{ docNumber: string; invoiceId: string } | null>(null);

  const itemsQ = trpc.items.list.useQuery({ usage: "sale", limit: 60, q: search || undefined });
  const sellable = useMemo(
    () => (itemsQ.data?.items ?? []).filter((i) => i.defaultRatePaise),
    [itemsQ.data],
  );

  const quoteLines = cart.map((l) => ({
    itemId: l.itemId,
    description: l.name,
    hsn: l.hsn,
    unit: l.unit,
    qtyMilli: l.qty * 1000,
    mrpPaise: l.mrpPaise,
    taxRateBps: l.taxRateBps,
    discountBps: 0,
  }));
  const quoteQ = trpc.pos.quote.useQuery({ lines: quoteLines }, { enabled: cart.length > 0 });

  const ring = trpc.pos.ringSale.useMutation({
    onSuccess: (data) => {
      setDone({ docNumber: data.docNumber, invoiceId: data.invoiceId });
      setCart([]);
      setPaying(false);
      toast.success(`Sale ${data.docNumber} — paid`, { description: "Posted to the books." });
    },
    onError: (e) => toast.error("Couldn't complete the sale", { description: e.message }),
  });

  function addItem(it: (typeof sellable)[number]) {
    setDone(null);
    setCart((prev) => {
      const found = prev.find((l) => l.itemId === it.id);
      if (found) return prev.map((l) => (l.itemId === it.id ? { ...l, qty: l.qty + 1 } : l));
      return [
        ...prev,
        {
          itemId: it.id,
          name: it.name,
          hsn: it.hsn && it.hsn.length >= 4 ? it.hsn : "9999",
          unit: it.unit || "Nos",
          mrpPaise: it.defaultRatePaise as string,
          taxRateBps: it.defaultTaxRateBps ?? 0,
          qty: 1,
        },
      ];
    });
  }
  const setQty = (id: string, d: number) =>
    setCart((prev) =>
      prev
        .map((l) => (l.itemId === id ? { ...l, qty: l.qty + d } : l))
        .filter((l) => l.qty > 0),
    );

  const grandPaise = quoteQ.data?.grandTotalPaise ?? "0";

  function charge() {
    if (cart.length === 0 || !quoteQ.data) return;
    setPaying(true);
  }
  function completeSale(tenders: Tender[]) {
    if (!quoteQ.data) return;
    ring.mutate({ issuedOn: today(), dueOn: today(), lines: quoteLines, tenders });
  }

  const cashierName = me.data?.user?.name ?? "Cashier";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg text-ink">
      {/* Top bar */}
      <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line bg-paper px-4">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-ink text-sm font-extrabold text-white">
          F
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold">Flint POS</div>
          <div className="text-xs text-muted">{me.data?.company?.legalName ?? "Outlet"}</div>
        </div>
        <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2.5 py-1 text-xs font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-ok" /> Register 1
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-slate-700 text-xs font-bold text-white">
            {initials(cashierName)}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">{cashierName}</div>
            <div className="text-xs text-muted">Cashier</div>
          </div>
          {onReturns && (
            <button
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-paper px-3 text-sm font-semibold text-ink-2 hover:border-bad hover:bg-bad-50 hover:text-bad"
              title="Returns & refunds"
              onClick={onReturns}
            >
              ↩ Returns
            </button>
          )}
          {/* An open shift leaves only via Close register (the Z-report) — no
              free "exit" that would abandon the till with sales unreconciled. */}
          <button
            className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-md bg-ink px-4 text-sm font-bold text-white hover:bg-ink-2"
            title="Close register (end shift) — runs the Z-report"
            onClick={() => (onCloseShift ? onCloseShift() : navigate({ to: "/" }))}
          >
            Close register
          </button>
        </div>
      </header>

      <div
        className="min-h-0 flex-1"
        style={{ display: "grid", gridTemplateColumns: "minmax(0,1.6fr) minmax(360px,0.95fr)" }}
      >
        {/* Catalog */}
        <section className="flex min-h-0 flex-col bg-bg">
          <div className="flex-shrink-0 px-4 pb-2 pt-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any style / SKU…"
              className="h-12 w-full rounded-lg border border-line-2 bg-paper px-4 text-base font-medium text-ink placeholder:font-normal placeholder:text-muted focus:border-sky-600 focus:outline-none focus:ring-4 focus:ring-sky-100"
            />
          </div>
          <div className="flex-shrink-0 px-4 pb-2 text-sm font-bold">★ Quick picks</div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {itemsQ.isLoading ? (
              <div className="p-10 text-center text-sm text-muted">Loading items…</div>
            ) : sellable.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted">
                No sellable items in this workspace yet. Add items in Catalogue.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px" }}>
                {sellable.map((it) => (
                  <button
                    key={it.id}
                    data-tile
                    onClick={() => addItem(it)}
                    className="flex min-h-[104px] flex-col rounded-lg border border-line bg-paper p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-line-2 hover:shadow-md active:scale-[.985]"
                  >
                    <div className="text-sm font-semibold leading-tight">{it.name}</div>
                    <div className="mt-0.5 text-xs text-muted">
                      {it.unit} · GST {(it.defaultTaxRateBps ?? 0) / 100}%
                    </div>
                    <div className="mt-auto pt-2 font-mono text-lg font-bold tracking-tight">
                      {formatRupees(it.defaultRatePaise)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Cart */}
        <aside className="flex min-h-0 flex-col border-l border-line bg-paper">
          <div className="flex flex-shrink-0 items-center gap-3 border-b border-line px-4 py-3">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-sky-50 text-sky-700">🛍️</div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Walk-in</div>
              <div className="text-xs text-muted">Counter sale · no GSTIN</div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {done ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
                <div className="grid h-16 w-16 place-items-center rounded-full bg-ok-50 text-3xl">✓</div>
                <div className="text-lg font-bold">Sale complete</div>
                <div className="font-mono text-sm text-muted">{done.docNumber}</div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-ok-100 bg-ok-50 px-3 py-1 text-xs font-bold text-ok">
                  PAID · posted to books
                </span>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() =>
                      window.open(
                        `${WEB_ENV.VITE_FLINT_API_URL}/api/pos-receipt/${done.invoiceId}/pdf`,
                        "_blank",
                        "noopener",
                      )
                    }
                    className="inline-flex h-11 items-center gap-2 rounded-lg border border-line bg-paper px-4 text-sm font-semibold text-ink-2 hover:bg-bg"
                  >
                    🖨 Print receipt
                  </button>
                  <button
                    onClick={() => setDone(null)}
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
                  >
                    + New sale
                  </button>
                </div>
              </div>
            ) : cart.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted">
                <div className="text-4xl opacity-40">🧾</div>
                <div className="text-sm font-semibold text-ink-2">No items yet</div>
                <p className="max-w-[200px] text-xs">Tap a style to start the sale.</p>
              </div>
            ) : (
              cart.map((l) => (
                <div key={l.itemId} className="border-b border-line px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">{l.name}</div>
                    <div className="font-mono text-sm font-bold">
                      {formatRupees((BigInt(l.mrpPaise) * BigInt(l.qty)).toString())}
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="font-mono text-xs text-muted">
                      {formatRupees(l.mrpPaise)} × {l.qty}
                    </div>
                    <div className="inline-flex overflow-hidden rounded-md border border-line">
                      <button className="grid h-8 w-8 place-items-center hover:bg-bg" onClick={() => setQty(l.itemId, -1)}>
                        −
                      </button>
                      <span className="grid w-9 place-items-center font-mono text-sm font-bold">{l.qty}</span>
                      <button className="grid h-8 w-8 place-items-center hover:bg-bg" onClick={() => setQty(l.itemId, +1)}>
                        +
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Totals + Charge */}
          <div className="flex-shrink-0 border-t border-line px-4 pb-4 pt-3">
            <div className="space-y-1 text-sm">
              <Row label="Subtotal" value={formatRupees(quoteQ.data?.subtotalPaise ?? "0")} />
              <Row label="CGST + SGST (GST)" value={formatRupees(
                ((BigInt(quoteQ.data?.cgstPaise ?? "0") + BigInt(quoteQ.data?.sgstPaise ?? "0") + BigInt(quoteQ.data?.igstPaise ?? "0"))).toString(),
              )} />
              <Row label="Round-off" value={formatRupees(quoteQ.data?.roundOffPaise ?? "0")} />
            </div>
            <div className="mt-2 flex items-baseline justify-between border-t border-line pt-3">
              <span className="text-xs font-bold uppercase tracking-wider">Total</span>
              <span className="font-mono text-3xl font-bold tracking-tight">{formatRupees(grandPaise)}</span>
            </div>
            <button
              className="mt-3 flex h-16 w-full items-center justify-between rounded-lg bg-ink px-6 text-base font-bold text-white shadow-md transition hover:bg-ink-2 disabled:pointer-events-none disabled:opacity-40"
              disabled={cart.length === 0 || ring.isPending || !quoteQ.data}
              onClick={charge}
            >
              <span>{ring.isPending ? "Charging…" : "Charge"}</span>
              <span className="font-mono text-lg">{formatRupees(grandPaise)} →</span>
            </button>
          </div>
        </aside>
      </div>

      {paying && quoteQ.data && (
        <PaymentSheet
          grandPaise={quoteQ.data.grandTotalPaise}
          pending={ring.isPending}
          onComplete={completeSale}
          onCancel={() => setPaying(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted">
      <span>{label}</span>
      <span className="font-mono font-medium text-ink-2">{value}</span>
    </div>
  );
}
